import axios from "axios";
import { saveAs } from "file-saver";
import { showErrorAlert } from "../components/shared/alerts";
import { BASE_URL, token } from "./index";

/**
 * Service for handling authenticated file operations with the new endpoints
 */

/**
 * Download a file using the download endpoint
 * @param filePath - The file path in private storage
 * @param fileName - Optional custom filename for download
 * @returns Promise<void>
 */
export const downloadSecureFile = async (
  filePath: string,
  fileName?: string
): Promise<void> => {
  try {
    const encodedPath = encodeURIComponent(filePath);
    const response = await axios.get(`${BASE_URL}/files/download?path=${encodedPath}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Get the file name from the header or use the provided filename
    const contentDisposition = response.headers['content-disposition'];
    const headerFilename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '');
    const finalFilename = fileName || headerFilename || 'download';

    if (response.status === 200) {
      saveAs(response.data, finalFilename);
    } else {
      console.error('Failed to download the file');
      showErrorAlert('Failed to download the file');
    }
  } catch (error: any) {
    console.error('Error downloading file:', error);
    showErrorAlert(error.response?.data?.message || 'Error downloading file');
  }
};

/**
 * Fetch a secure image as a blob for display (e.g. in AuthenticatedImage)
 * @param filePath - The file path in private storage
 * @returns Promise<Blob> - The image blob
 */
export const fetchSecureImage = async (filePath: string): Promise<Blob> => {
  const encodedPath = encodeURIComponent(filePath);
  const response = await axios.get(`${BASE_URL}/files/image?path=${encodedPath}`, {
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const contentType = response.headers?.["content-type"] || "";
  if (!contentType.startsWith("image/")) {
    throw new Error(`Expected image but got ${contentType}`);
  }

  return response.data;
};